import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  format as formatDate,
  isAfter,
  isBefore,
  isEqual as isEqualDate,
  lastDayOfMonth,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns'
import * as React from 'react'

import useKeyboardEvent from '../../hooks/useKeyboardEvent'
import Icon from '../icon'
import VisuallyHidden from '../visually-hidden'
import { ISO_DATE_FORMAT, KeyCodes } from './date-input'
import styles from './date-input.module.scss'
import Day from './day'

const DAYS_OF_WEEK = [
  { abbr: 'Monday', text: 'M' },
  { abbr: 'Tuesday', text: 'T' },
  { abbr: 'Wednesday', text: 'W' },
  { abbr: 'Thursday', text: 'T' },
  { abbr: 'Friday', text: 'F' },
  { abbr: 'Saturday', text: 'S' },
  { abbr: 'Sunday', text: 'S' },
]

// this produces an array of dates starting from the fromDate, and going up to the toDate (inclusive)
function generateDateRange(fromDate: Date, toDate: Date): Array<Date> {
  const result = []
  for (let i = fromDate.getDate(); i <= toDate.getDate(); i++) {
    result.push(new Date(fromDate.getFullYear(), fromDate.getMonth(), i))
  }
  return result
}

type Props = {
  selectedDate: Date | null | undefined
  setSelectedDate: (arg0: string) => void
  hidePicker: () => void
  max?: string | number
  min?: string | number
}

// adapted from https://www.w3.org/TR/wai-aria-practices/examples/dialog-modal/js/datepicker.js
export default function Datepicker({
  selectedDate,
  setSelectedDate,
  hidePicker,
  max,
  min,
}: Props) {
  //  more refs for managing event listeners and focus
  const pickerRef = React.useRef<HTMLDivElement>(null)
  const prevYearRef = React.useRef<HTMLButtonElement>(null)

  // default to today if no date is passed in.
  // dateShown is not necessarily the value of the date input
  // it represents where the user has navigated to using arrow keys
  const [dateShown, setDateShown] = React.useState(
    selectedDate || startOfDay(new Date()),
  )

  // this is the current month displayed - again, not necessarily reflective of input value
  const [monthInView, setMonthInView] = React.useState(
    selectedDate || new Date(),
  )

  const maxDate = max != null ? startOfDay(new Date(max)) : null
  const minDate = min != null ? startOfDay(new Date(min)) : null

  React.useEffect(() => {
    if (selectedDate != null) {
      setMonthInView(selectedDate)
      setDateShown(selectedDate)
    }
  }, [selectedDate])

  // this is a snazzy hook I found on the interwebs which allows us to
  // easily set event listeners for various keypresses
  useKeyboardEvent(KeyCodes.ESC, hidePicker)
  useKeyboardEvent(KeyCodes.LEFT, () => moveFocus(dateShown, subDays))
  useKeyboardEvent(KeyCodes.RIGHT, () => moveFocus(dateShown, addDays))
  useKeyboardEvent(KeyCodes.UP, () => moveFocus(dateShown, subWeeks))
  useKeyboardEvent(KeyCodes.DOWN, () => moveFocus(dateShown, addWeeks))

  // this is called when the user hits tab on the "Today" button at the bottom
  // we want to trap focus inside the datepicker, so we return the focus to the first focusable element
  function maybeReturnFocusToTop(e: React.KeyboardEvent) {
    if (e.keyCode === KeyCodes.TAB) {
      prevYearRef.current && prevYearRef.current.focus()
      e.stopPropagation()
      e.preventDefault()
    }
  }

  function moveFocus(date: Date, dateFn: (arg0: Date, arg1: number) => Date) {
    const newSelectedDate = dateFn(date, 1)
    if (newSelectedDate.getMonth() !== date.getMonth()) {
      setMonthInView(newSelectedDate)
    }
    setDateShown(newSelectedDate)
  }

  // when we open the picker, set up event listener to close the picker on body element click
  React.useEffect(() => {
    // there's an event listener on the document body that will hide the picker
    // but if the click happened inside the picker, we don't want to hide it.
    function hidePickerOnBackgroundClick(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        hidePicker()
        e.stopPropagation()
        e.preventDefault()
      }
    }

    document.addEventListener('click', hidePickerOnBackgroundClick)

    return () => {
      document.removeEventListener('click', hidePickerOnBackgroundClick)
    }
    // we only want this to run once, on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // the first day in the current month
  const firstOfMonth = startOfMonth(monthInView)
  const lastOfMonth = lastDayOfMonth(monthInView)
  // the day of the week of the first day
  const firstOfMonthDay = firstOfMonth.getDay()

  // Because months start on different days, we need to calculate how many days from the previous month
  // we need to show leading up to the first day of the current month, if it doesn't start on a Monday.
  // Days are zero-indexed and 0 is Sunday, because America.
  // If the first of the month falls on a Sunday, we want to show the 6 days prior (i.e. from the previous Monday.)
  // If the first of the month isn't a Sunday, we do (firstOfMonth - firstOfMonthDay - 1) to get the week starting on Monday.
  // e.g if 1st of the month is Wednesday (index 3), and we want the previous Monday (index 1), we subtract 2 days from the first of the month.
  const firstVisibleDay =
    firstOfMonthDay === 0 // Sunday
      ? subDays(firstOfMonth, 6)
      : subDays(firstOfMonth, firstOfMonthDay - 1)
  const visibleDatesFromLastMonth = !isEqualDate(firstOfMonth, firstVisibleDay)
    ? generateDateRange(firstVisibleDay, lastDayOfMonth(firstVisibleDay))
    : []

  const datesForThisMonth = generateDateRange(firstOfMonth, lastOfMonth)

  const allDates = [...visibleDatesFromLastMonth, ...datesForThisMonth]

  // Calendar is a 7x5 grid, so 35 spaces...
  // UNLESS it's a month starting on a Sunday, in which case it's a 7 x 6 grid, with 42 spaces.
  const numberOfGridSpaces = firstOfMonthDay === 0 ? 42 : 35

  // If there aren't enough dates in our array to fill the entire grid, we need to pad the end with
  // dates from next month.
  if (allDates.length < numberOfGridSpaces) {
    const numberOfEmptySpaces = numberOfGridSpaces - allDates.length
    allDates.push(
      ...generateDateRange(
        addDays(lastOfMonth, 1),
        addDays(lastOfMonth, numberOfEmptySpaces),
      ),
    )
  }

  const dateRows = []
  // A for loop! How retro!
  for (let i = 0; i < allDates.length; i += 7) {
    dateRows.push(allDates.slice(i, i + 7))
  }

  function onSelectDate(date: Date) {
    setSelectedDate(formatDate(date, ISO_DATE_FORMAT))
    hidePicker()
  }

  return (
    <div
      id="id-datepicker-1"
      className={styles['datepicker-dialog']}
      role="dialog"
      aria-modal="true"
      ref={pickerRef}
      aria-labelledby="id-dialog-label"
    >
      <div className={styles.header}>
        <button
          className={styles.prevYear}
          type="button"
          onClick={() => setMonthInView(addYears(monthInView, -1))}
          ref={prevYearRef}
          disabled={
            minDate != null &&
            subYears(monthInView, 1).getFullYear() < minDate.getFullYear()
          }
        >
          <VisuallyHidden is="span">Previous year</VisuallyHidden>
          <Icon size={16} icon="chevron-double-left" color="black" />
        </button>
        <button
          className={styles.prevMonth}
          type="button"
          disabled={
            minDate != null &&
            isBefore(lastDayOfMonth(subMonths(monthInView, 1)), minDate)
          }
          onClick={() => setMonthInView(subMonths(monthInView, 1))}
        >
          <VisuallyHidden is="span">Previous month</VisuallyHidden>
          <Icon size={16} icon="chevron-left" color="black" />
        </button>
        <h2
          id="id-dialog-label"
          className={styles.monthYear}
          aria-live="polite"
        >
          {formatDate(monthInView, 'MMMM yyyy')}
        </h2>
        <button
          className={styles.nextMonth}
          aria-label="next month"
          type="button"
          onClick={() => setMonthInView(addMonths(monthInView, 1))}
          disabled={
            maxDate != null &&
            isAfter(startOfMonth(addMonths(monthInView, 1)), maxDate)
          }
        >
          <VisuallyHidden is="span">Next month</VisuallyHidden>
          <Icon size={16} icon="chevron-right" color="black" />
        </button>
        <button
          className={styles.nextYear}
          type="button"
          onClick={() => setMonthInView(addYears(monthInView, 1))}
          disabled={
            maxDate != null &&
            addYears(monthInView, 1).getFullYear() > maxDate.getFullYear()
          }
        >
          <VisuallyHidden is="span">Next year</VisuallyHidden>
          <Icon size={16} icon="chevron-double-right" color="black" />
        </button>
      </div>
      <table
        className={styles.dates}
        role="grid"
        aria-labelledby="id-dialog-label"
      >
        <thead>
          <tr>
            {DAYS_OF_WEEK.map((day) => (
              <th scope="col" abbr={day.abbr} key={day.abbr}>
                {day.text}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dateRows.map((row, index) => (
            <tr key={`row-${index}`}>
              {row.map((date) => (
                <Day
                  onClick={() => onSelectDate(date)}
                  key={`day-${date.toString()}`}
                  selected={isEqualDate(date, dateShown)}
                  inCurrentMonth={date.getMonth() === monthInView.getMonth()}
                  date={date}
                  disabled={
                    (minDate != null && isBefore(date, minDate)) ||
                    (maxDate != null && isAfter(date, maxDate))
                  }
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        className={styles['today-button']}
        onClick={() => setSelectedDate(formatDate(new Date(), ISO_DATE_FORMAT))}
        onKeyDown={maybeReturnFocusToTop}
      >
        Today
      </button>
      <VisuallyHidden is={'span'} aria-live="polite">
        Use arrow keys to navigate dates
      </VisuallyHidden>
    </div>
  )
}
